import { getFileExtension } from '@repo/lib/fs/getFileExtension';
import { prettyBytes } from '@repo/lib/prettyBytes';
import { FileUp, FolderUp, Trash2Icon } from 'lucide-react';

import type { FileUploadActions, FileUploadState } from '@/hooks/file-upload';

import { Button } from '@/components/ui/button';
import { FileIcon, FolderIcon } from '@/components/ui/file-icon';
import { FileCard, FileCardActionButton, FileCardContent, FileCardPreview, FileCardPreviewFallback, FileCardPreviewImage, FileCardTitle } from '@/components/ui/fs-card/FileCard';
import { DropzoneContent, DropzoneDragArea } from '@/components/ui/fs-dropzone';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface FsUploadFilesProps {
  state: FileUploadState;
  actions: FileUploadActions;
  options: {
    allow: { file?: boolean; directory?: boolean };
    multiple?: boolean;
    accept?: string;
    maxFiles?: number;
    maxSize?: number;
  };
}

export default function FsUploadFiles(fsUploadFilesProps: FsUploadFilesProps) {
  const { state, actions, options } = fsUploadFilesProps;
  const { allow, maxSize, accept, multiple } = options;

  const fileInput = actions.createInputProps({
    'aria-label': 'Upload file attachments',
    multiple: multiple,
    accept: accept,
    maxSize: maxSize,
  });
  const directoryInput = actions.createInputProps({
    'aria-label': 'Upload directory',
    directory: '',
    webkitdirectory: '',
    accept: accept,
  });

  const dropzoneConfig = actions.createDropzoneProps({
    className: 'py-6 w-full',
    accept: accept,
    multiple: multiple,
  });

  return (
    <div className="flex h-full">
      <input id="file-upload" ref={fileInput.ref} className="sr-only" {...fileInput.props} />
      <input
        id="dir-upload"
        ref={directoryInput.ref}
        className="sr-only"
        {...{
          directory: '',
          webkitdirectory: '',
        }}
        {...directoryInput.props}
      />
      <DropzoneDragArea isDragging={state.isDragging} {...dropzoneConfig.props} onClick={state.files.length === 0 && allow.file ? fileInput.openFileDialog : undefined}>
        {state.files.length === 0 ? (
          <>
            <DropzoneContent maxFileSize={maxSize} />
            <div className="mt-4 flex justify-center gap-2">
              {allow.file && (
                <Button
                  variant="outline"
                  onClick={(e) => {
                    fileInput.openFileDialog();
                    e.stopPropagation();
                  }}
                >
                  <FileUp />
                  Prześlij plik
                </Button>
              )}
              {allow.directory && (
                <Button
                  variant="outline"
                  onClick={(e) => {
                    directoryInput.openFileDialog();
                    e.stopPropagation();
                  }}
                >
                  <FolderUp />
                  Prześlij folder
                </Button>
              )}
            </div>
          </>
        ) : (
          <div className="flex w-full flex-col gap-5">
            <div className="flex w-full items-center justify-between gap-2">
              <h3 className="truncate text-sm font-medium">Pliki ({state.files.length})</h3>
              <div className="flex w-fit gap-2">
                {allow.file && (
                  <Tooltip>
                    <TooltipTrigger asChild onClick={fileInput.openFileDialog}>
                      <Button variant="outline">
                        <FileUp />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Prześlij plik</p>
                    </TooltipContent>
                  </Tooltip>
                )}
                {allow.directory && (
                  <Tooltip>
                    <TooltipTrigger asChild onClick={directoryInput.openFileDialog}>
                      <Button variant="outline">
                        <FolderUp />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Prześlij folder</p>
                    </TooltipContent>
                  </Tooltip>
                )}
                <Tooltip>
                  <TooltipTrigger asChild onClick={actions.clearFiles}>
                    <Button variant="outline">
                      <Trash2Icon />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Usuń wszystkie</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
              {state.directories.map((directory) => {
                const isIdle: boolean = directory.status === 'idle';
                return (
                  <FileCard key={directory.id} className="w-full p-0">
                    <FileCardActionButton onClick={() => actions.removeDirectory(directory.id)} className={!isIdle ? 'hidden' : ''} />
                    <FileCardContent className="flex-col gap-0">
                      <FileCardPreview className="rounded-none border-0 border-b">
                        <div className="flex h-full items-center justify-center">
                          <FolderIcon size="lg" className="border-none bg-transparent" />
                        </div>
                      </FileCardPreview>
                      <div className="w-full p-2">
                        <FileCardTitle className="text-text-secondary text-sm">{directory.name}</FileCardTitle>
                        <span className="text-text-tertiary truncate text-xs">plików: {directory.filesCount} </span>
                      </div>
                    </FileCardContent>
                  </FileCard>
                );
              })}
              {state.files.map((file) => {
                const isIdle: boolean = file.status === 'idle';
                return (
                  <FileCard key={file.id} className="w-full p-0">
                    <FileCardActionButton onClick={() => actions.removeFile(file.id)} className={!isIdle ? 'hidden' : ''} />
                    <FileCardContent className="flex-col gap-0">
                      <FileCardPreview className="relative rounded-none border-0 border-b">
                        <FileCardPreviewImage className="rounded-t-md" src={file.preview} />
                        <FileCardPreviewFallback>
                          <FileIcon size="lg" className="border-none bg-transparent" fileExtension={getFileExtension(file.file.name)} />
                        </FileCardPreviewFallback>
                      </FileCardPreview>
                      <div className="w-full p-2">
                        <FileCardTitle className="text-text-secondary text-sm">{file.file.name}</FileCardTitle>
                        <span className="text-text-tertiary truncate text-xs">{prettyBytes(file.file.size)}</span>
                      </div>
                    </FileCardContent>
                  </FileCard>
                );
              })}
            </div>
          </div>
        )}
      </DropzoneDragArea>
    </div>
  );
}
